<template>
    <div class="flex min-h-full flex-1 flex-col justify-start px-6 py-12 lg:px-8">
        <div class="sm:mx-auto sm:w-full sm:max-w-sm">
            <h2 class="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900">Sign up</h2>
        </div>

        <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-lg">
            <form class="space-y-6" action="#" method="POST" @submit.prevent="submitForm">
                <div v-if="errors.general" class="mb-4 text-sm text-red-600">
                    {{ errors.general }}
                </div>
                <div>
                    <div class="mt-2">
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="firstName" v-model="user.firstName" variant="filled" class="w-full" required
                                :invalid="!!errors.firstName" type="text" @focus="errors.firstName = null" />
                            <label for="firstName">First Name</label>
                        </FloatLabel>
                        <div v-if="errors.firstName" class="mt-1 text-sm text-red-600">
                            {{ errors.firstName }}
                        </div>
                    </div>
                </div>

                <div>
                    <div class="mt-2">
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="lastName" v-model="user.lastName" variant="filled" class="w-full" required
                                :invalid="!!errors.lastName" type="text" @focus="errors.lastName = null" />
                            <label for="lastName">Last Name</label>
                        </FloatLabel>
                        <div v-if="errors.lastName" class="mt-1 text-sm text-red-600">
                            {{ errors.lastName }}
                        </div>
                    </div>
                </div>

                <div>
                    <div class="mt-2">
                        <FloatLabel variant="in" class="w-full">
                            <Select id="gender" v-model="user.gender" variant="filled" class="w-full" required
                                option-label="label" option-value="value" :options="genderOptions"
                                :highlightOnSelect="true" :invalid="!!errors.gender" @focus="errors.gender = null">
                                <template #option="{ option }">
                                    <span>{{ option?.label || 'Select Gender' }}</span>
                                </template>
                            </Select>
                            <label for="gender">Gender</label>
                        </FloatLabel>
                        <div v-if="errors.gender" class="mt-1 text-sm text-red-600">
                            {{ errors.gender }}
                        </div>
                    </div>
                </div>

                <div>
                    <div class="mt-2">
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="phone" v-model="user.phone" variant="filled" class="w-full" type="tel" required
                                :invalid="!!errors.phone" @focus="errors.phone = null" />
                            <label for="phone">Phone</label>
                        </FloatLabel>
                        <div v-if="errors.phone" class="mt-1 text-sm text-red-600">
                            {{ errors.phone }}
                        </div>
                    </div>
                </div>

                <div>
                    <div class="mt-2">
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="whatsapp" v-model="user.whatsapp" variant="filled" class="w-full" type="tel" required
                                :invalid="!!errors.whatsapp" @focus="errors.whatsapp = null" />
                            <label for="whatsapp">WhatsApp</label>
                        </FloatLabel>
                        <div v-if="errors.whatsapp" class="mt-1 text-sm text-red-600">
                            {{ errors.whatsapp }}
                        </div>
                    </div>
                </div>

                <div>
                    <div class="mt-2">
                        <FloatLabel variant="in" class="w-full">
                            <Select id="country_id" v-model="user.country_id" variant="filled" class="w-full" type="text" required
                                :invalid="!!errors.country_id" @focus="errors.country_id = null" :options="countries"
                                option-label="name" option-value="id">
                                <template #option="{ option }">
                                    <span>{{ option?.name }}</span>
                                </template>
                            </Select>
                        </FloatLabel>
                        <div v-if="errors.country_id" class="mt-1 text-sm text-red-600">
                            {{ errors.country_id }}
                        </div>
                    </div>
                </div>

                <div>
                    <div class="mt-2">
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="age" v-model="user.age" variant="filled" class="w-full" type="number" required
                                :invalid="!!errors.age" @focus="errors.age = null" max="100" />
                            <label for="age">Age</label>
                        </FloatLabel>
                        <div v-if="errors.age" class="mt-1 text-sm text-red-600">
                            {{ errors.age }}
                        </div>
                    </div>
                </div>

                <div>
                    <div class="mt-2">
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="email" v-model="user.email" variant="filled" class="w-full" type="email" required
                                :invalid="!!errors.email" @focus="errors.email = null" />
                            <label for="email">Email</label>
                        </FloatLabel>
                        <div v-if="errors.email" class="mt-1 text-sm text-red-600">
                            {{ errors.email }}
                        </div>
                    </div>
                </div>

                <div>
                    <div class="mt-2">
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="password" v-model="user.password" variant="filled" class="w-full" required
                                type="password" :invalid="!!errors.password" @focus="errors.password = null" />
                            <label for="password">Password</label>
                        </FloatLabel>
                        <div v-if="errors.password" class="mt-1 text-sm text-red-600">
                            {{ errors.password }}
                        </div>
                    </div>
                </div>

                <div>
                    <Button class="w-full" type="submit" variant="primary" :loading="loading">
                        Register
                    </Button>
                </div>
            </form>
        </div>

        <div class="mt-6 text-center text-sm text-gray-500">
            Already have an account? <a href="/login" class="font-semibold text-emerald-600 hover:text-emerald-500">Sign
                in</a>
        </div>
    </div>
</template>

<script setup>

import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import Button from 'primevue/button';
import Select from 'primevue/select';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { getCountries } from '@/services/countryService';

const route = useRouter();
const user = ref({});

const errors = ref({});

const genderOptions = ref([
    { label: 'Select Gender', value: null, disabled: true },
    { label: 'Male', value: 1 },
    { label: 'Female', value: 2 },
    { label: 'Prefer not to say', value: 0 }
]);

const loading = ref(false);
const countries = ref([]);

getCountries().then(response => {
    countries.value = response.data.map(country => ({
        id: country.id,
        name: country.name,
        phone_code: country.phone_code,
    }));

    countries.value.unshift({ id: null, name: 'Select Country', phone_code: '', flag: '', disabled: true });

    console.log('Countries loaded:', countries.value);
}).catch(error => {
    console.error('Failed to fetch countries:', error);
    errors.value.general = 'Failed to load countries. Please try again later.';
});

const submitForm = () => {
    loading.value = true;

    useAuth()
        .register(user.value)
        .then(response => {
            route.push({ name: 'login' });
        })
        .catch(error => {
            if (error.response?.status === 422) {
                const validationErrors = error.response?.data?.errors;
                for (const key in validationErrors) {
                    if (Object.prototype.hasOwnProperty.call(validationErrors, key)) {
                        errors.value[key] = validationErrors[key][0];
                    }
                }
            } else {
                errors.value.general = error.response?.data?.message ?? 'An unexpected error occurred. Please try again later.';
                console.error('Registration failed:', error.response?.data?.message || error.message);
            }
        }).finally(() => {
            loading.value = false;
        });
};

</script>